import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useOperator } from '@/lib/operator-context';
import { apiFetch } from '@/lib/operator-api';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import {
  MessageSquare,
  Phone,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Sparkles,
  Building2,
  PartyPopper,
  X,
  AlertCircle,
  Clock,
  Zap,
  Scissors,
  Dumbbell,
  Stethoscope,
  ShoppingBag,
  Heart,
  Store,
  Plus,
  Trash2,
  Mail,
  MapPin,
  FileText,
  Users,
} from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

type WizardStep =
  | 'BUSINESS_PROFILE'
  | 'CONTACT_INFO'
  | 'SERVICES'
  | 'BUSINESS_HOURS'
  | 'TEAM_SETUP'
  | 'WHATSAPP_SETUP'
  | 'TEMPLATES'
  | 'COMPLETE';

type BusinessType =
  | 'BARBERSHOP'
  | 'BEAUTY_SALON'
  | 'HAIR_SALON'
  | 'NAIL_SALON'
  | 'SPA'
  | 'RETAIL_STORE'
  | 'FITNESS_STUDIO'
  | 'MEDICAL_PRACTICE'
  | 'OTHER';

interface ServiceEntry {
  id?: string;
  name: string;
  description: string;
  durationMinutes: number;
  priceCents: number;
}

interface DayHours {
  open: string;
  close: string;
  enabled: boolean;
}

interface TemplateEntry {
  id: string;
  slug: string;
  bodyText: string;
  isActive: boolean;
}

interface StaffMember {
  id?: string;
  name: string;
  email?: string;
  phone?: string;
  role: 'OWNER' | 'STAFF';
  color?: string;
  availability: Array<{ dayOfWeek: number; isAvailable: boolean; slots: { start: string; end: string }[] }>;
  serviceIds: string[];
  isNew: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const STEPS: WizardStep[] = [
  'BUSINESS_PROFILE',
  'CONTACT_INFO',
  'SERVICES',
  'BUSINESS_HOURS',
  'TEAM_SETUP',
  'WHATSAPP_SETUP',
  'TEMPLATES',
  'COMPLETE',
];

const STEP_ICONS: Record<WizardStep, React.ComponentType<{ className?: string }>> = {
  BUSINESS_PROFILE: Building2,
  CONTACT_INFO: Phone,
  SERVICES: Scissors,
  BUSINESS_HOURS: Clock,
  TEAM_SETUP: Users,
  WHATSAPP_SETUP: MessageSquare,
  TEMPLATES: FileText,
  COMPLETE: PartyPopper,
};

const BUSINESS_TYPE_CONFIG: Record<BusinessType, {
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
}> = {
  BARBERSHOP: { labelKey: 'onboarding.businessTypes.barbershop', icon: Scissors },
  BEAUTY_SALON: { labelKey: 'onboarding.businessTypes.beautySalon', icon: Sparkles },
  HAIR_SALON: { labelKey: 'onboarding.businessTypes.hairSalon', icon: Scissors },
  NAIL_SALON: { labelKey: 'onboarding.businessTypes.nailSalon', icon: Heart },
  SPA: { labelKey: 'onboarding.businessTypes.spa', icon: Sparkles },
  RETAIL_STORE: { labelKey: 'onboarding.businessTypes.retailStore', icon: ShoppingBag },
  FITNESS_STUDIO: { labelKey: 'onboarding.businessTypes.fitnessStudio', icon: Dumbbell },
  MEDICAL_PRACTICE: { labelKey: 'onboarding.businessTypes.medicalPractice', icon: Stethoscope },
  OTHER: { labelKey: 'onboarding.businessTypes.other', icon: Store },
};

const DEFAULT_HOURS: Record<string, DayHours> = {
  monday: { open: '09:00', close: '18:00', enabled: true },
  tuesday: { open: '09:00', close: '18:00', enabled: true },
  wednesday: { open: '09:00', close: '18:00', enabled: true },
  thursday: { open: '09:00', close: '18:00', enabled: true },
  friday: { open: '09:00', close: '18:00', enabled: true },
  saturday: { open: '09:00', close: '13:00', enabled: true },
  sunday: { open: '09:00', close: '13:00', enabled: false },
};

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

// ============================================================================
// API Helpers
// ============================================================================

const onboardingApi = {
  async getStatus(businessId: string) {
    const res = await apiFetch(
      `/api/operator/onboarding/status?businessId=${encodeURIComponent(businessId)}`,
    );
    if (!res.ok) return null;
    const json = await res.json();
    return json.data;
  },

  async startSession(businessId: string) {
    const res = await apiFetch('/api/operator/onboarding/start', {
      method: 'POST',
      body: JSON.stringify({ businessId }),
    });
    if (!res.ok) throw new Error('START_FAILED');
    const json = await res.json();
    return json.data;
  },

  async saveStep(businessId: string, stepNumber: number, data: Record<string, unknown>) {
    const res = await apiFetch(`/api/operator/onboarding/steps/${stepNumber}`, {
      method: 'PUT',
      body: JSON.stringify({ businessId, data }),
    });
    if (!res.ok) throw new Error('SAVE_STEP_FAILED');
    const json = await res.json();
    return json.data;
  },

  async updateBusinessProfile(businessId: string, data: { name?: string; timezone?: string }) {
    const res = await apiFetch(`/api/operator/businesses/${encodeURIComponent(businessId)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('UPDATE_BUSINESS_FAILED');
    const json = await res.json();
    return json.data;
  },

  async createService(businessId: string, service: ServiceEntry) {
    const res = await apiFetch('/api/operator/services', {
      method: 'POST',
      body: JSON.stringify({
        businessId,
        name: service.name,
        description: service.description,
        durationMinutes: service.durationMinutes,
        priceCents: service.priceCents,
      }),
    });
    // 409 = duplicate name, that is OK
    if (!res.ok && res.status !== 409) throw new Error('CREATE_SERVICE_FAILED');
    const json = await res.json();
    return json.data;
  },

  async getServices(businessId: string) {
    const res = await apiFetch(
      `/api/operator/services?businessId=${encodeURIComponent(businessId)}&pageSize=50`,
    );
    if (!res.ok) return [];
    const json = await res.json();
    return json.data || [];
  },

  async getBusinessHours(businessId: string) {
    const res = await apiFetch(
      `/api/operator/businesses/${encodeURIComponent(businessId)}/hours`,
    );
    if (!res.ok) return null;
    const json = await res.json();
    return json.data;
  },

  async saveBusinessHours(businessId: string, hours: Record<string, { open: string; close: string } | null>) {
    const res = await apiFetch(
      `/api/operator/businesses/${encodeURIComponent(businessId)}/hours`,
      {
        method: 'PUT',
        body: JSON.stringify(hours),
      },
    );
    if (!res.ok) throw new Error('SAVE_HOURS_FAILED');
    return true;
  },

  async getTemplates(businessId: string) {
    const res = await apiFetch(
      `/api/operator/templates?businessId=${encodeURIComponent(businessId)}&pageSize=50`,
    );
    if (!res.ok) return [];
    const json = await res.json();
    return json.data || [];
  },

  async setupDefaults(businessId: string, businessType: string) {
    const res = await apiFetch('/api/operator/onboarding/setup-defaults', {
      method: 'POST',
      body: JSON.stringify({ businessId, businessType }),
    });
    if (!res.ok) throw new Error('SETUP_DEFAULTS_FAILED');
    const json = await res.json();
    return json.data;
  },

  async completeOnboarding(businessId: string, sessionId?: string) {
    const res = await apiFetch('/api/operator/onboarding/complete', {
      method: 'POST',
      body: JSON.stringify(sessionId ? { sessionId } : { businessId }),
    });
    if (!res.ok) throw new Error('COMPLETE_FAILED');
  },

  async abandonSession(sessionId: string) {
    const res = await apiFetch('/api/operator/onboarding/abandon', {
      method: 'POST',
      body: JSON.stringify({ sessionId }),
    });
    if (!res.ok) throw new Error('ABANDON_FAILED');
  },
};

// ============================================================================
// Sub-Components
// ============================================================================

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );
}

function StepProgress({
  currentStep,
  steps,
}: {
  currentStep: WizardStep;
  steps: WizardStep[];
}) {
  const { t } = useTranslation();
  const currentIndex = steps.indexOf(currentStep);

  const stepLabels: Record<WizardStep, string> = {
    BUSINESS_PROFILE: t('onboarding.wizardSteps.businessProfile', 'Perfil'),
    CONTACT_INFO: t('onboarding.wizardSteps.contactInfo', 'Contato'),
    SERVICES: t('onboarding.wizardSteps.services', 'Serviços'),
    BUSINESS_HOURS: t('onboarding.wizardSteps.businessHours', 'Horários'),
    TEAM_SETUP: t('onboarding.wizardSteps.teamSetup', 'Equipe'),
    WHATSAPP_SETUP: t('onboarding.wizardSteps.whatsappSetup', 'WhatsApp'),
    TEMPLATES: t('onboarding.wizardSteps.templates', 'Templates'),
    COMPLETE: t('onboarding.wizardSteps.complete', 'Concluído'),
  };

  return (
    <div className="w-full">
      <div className="mb-6">
        <Progress
          value={((currentIndex + 1) / steps.length) * 100}
          className="h-2 bg-[hsl(160,60%,18%)]/10"
        />
      </div>
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const isCompleted = index < currentIndex;
          const isCurrent = index === currentIndex;
          const StepIcon = STEP_ICONS[step];
          return (
            <div key={step} className="flex flex-col items-center gap-1.5 flex-1">
              <div
                className={`
                  w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300
                  ${isCompleted
                    ? 'bg-[hsl(160,84%,39%)] text-white shadow-md'
                    : isCurrent
                      ? 'bg-[hsl(160,60%,18%)] text-white shadow-lg ring-4 ring-[hsl(160,84%,39%)]/20'
                      : 'bg-muted text-muted-foreground'
                  }
                `}
              >
                {isCompleted ? (
                  <CheckCircle2 className="h-4 w-4" />
                ) : (
                  <StepIcon className="h-4 w-4" />
                )}
              </div>
              <span
                className={`text-[10px] font-medium text-center leading-tight hidden sm:block ${
                  isCurrent
                    ? 'text-[hsl(160,60%,18%)]'
                    : isCompleted
                      ? 'text-[hsl(160,84%,39%)]'
                      : 'text-muted-foreground'
                }`}
              >
                {stepLabels[step]}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// Step Components
// ============================================================================

function BusinessProfileStep({
  businessName,
  setBusinessName,
  businessDescription,
  setBusinessDescription,
  businessCategory,
  setBusinessCategory,
  onNext,
  isSubmitting,
}: {
  businessName: string;
  setBusinessName: (v: string) => void;
  businessDescription: string;
  setBusinessDescription: (v: string) => void;
  businessCategory: BusinessType;
  setBusinessCategory: (v: BusinessType) => void;
  onNext: () => void;
  isSubmitting: boolean;
}) {
  const { t } = useTranslation();
  const canContinue = businessName.trim().length >= 2;

  return (
    <div className="space-y-6">
      <div className="text-center max-w-lg mx-auto">
        <div className="w-16 h-16 rounded-2xl bg-[hsl(160,84%,39%)]/10 flex items-center justify-center mx-auto mb-4">
          <Building2 className="h-8 w-8 text-[hsl(160,60%,18%)]" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-2">
          {t('onboarding.profile.title', 'Perfil do Negócio')}
        </h2>
        <p className="text-muted-foreground">
          {t('onboarding.profile.description', 'Informe os dados básicos do seu negócio para começarmos.')}
        </p>
      </div>

      <Card className="max-w-lg mx-auto">
        <CardContent className="pt-6 space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              {t('onboarding.profile.nameLabel', 'Nome do Negócio')}
            </label>
            <Input
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              placeholder={t('onboarding.profile.namePlaceholder', 'Ex: Barbearia do Carlos')}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              {t('onboarding.profile.descriptionLabel', 'Descrição (opcional)')}
            </label>
            <Textarea
              value={businessDescription}
              onChange={(e) => setBusinessDescription(e.target.value)}
              placeholder={t('onboarding.profile.descriptionPlaceholder', 'Breve descrição do seu negócio...')}
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              {t('onboarding.profile.categoryLabel', 'Tipo de Negócio')}
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {(Object.entries(BUSINESS_TYPE_CONFIG) as [BusinessType, typeof BUSINESS_TYPE_CONFIG[BusinessType]][]).map(
                ([type, config]) => {
                  const TypeIcon = config.icon;
                  const isSelected = businessCategory === type;
                  return (
                    <button
                      key={type}
                      onClick={() => setBusinessCategory(type)}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        isSelected
                          ? 'border-[hsl(160,84%,39%)] bg-[hsl(160,84%,39%)]/5'
                          : 'border-border hover:border-[hsl(160,60%,18%)]/30'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <TypeIcon className={`h-4 w-4 ${isSelected ? 'text-[hsl(160,84%,39%)]' : 'text-muted-foreground'}`} />
                        <span className={`text-xs font-medium ${isSelected ? 'text-[hsl(160,60%,18%)]' : 'text-foreground'}`}>
                          {t(config.labelKey, type.replace(/_/g, ' '))}
                        </span>
                      </div>
                    </button>
                  );
                },
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-center">
        <Button
          onClick={onNext}
          disabled={!canContinue || isSubmitting}
          size="lg"
          className="gap-2 bg-[hsl(160,60%,18%)] hover:bg-[hsl(160,60%,22%)] text-white px-8"
        >
          {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {t('common.next', 'Próximo')}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function ContactInfoStep({
  contactPhone,
  setContactPhone,
  contactEmail,
  setContactEmail,
  contactAddress,
  setContactAddress,
  onNext,
  onBack,
  isSubmitting,
}: {
  contactPhone: string;
  setContactPhone: (v: string) => void;
  contactEmail: string;
  setContactEmail: (v: string) => void;
  contactAddress: string;
  setContactAddress: (v: string) => void;
  onNext: () => void;
  onBack: () => void;
  isSubmitting: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <div className="text-center max-w-lg mx-auto">
        <div className="w-14 h-14 rounded-2xl bg-[hsl(160,84%,39%)]/10 flex items-center justify-center mx-auto mb-4">
          <Phone className="h-7 w-7 text-[hsl(160,60%,18%)]" />
        </div>
        <h2 className="text-xl font-bold text-foreground mb-2">
          {t('onboarding.contact.title', 'Informações de Contato')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('onboarding.contact.description', 'Como seus clientes podem entrar em contato com você.')}
        </p>
      </div>

      <Card className="max-w-lg mx-auto">
        <CardContent className="pt-6 space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground flex items-center gap-2">
              <Phone className="h-4 w-4 text-muted-foreground" />
              {t('onboarding.contact.phoneLabel', 'Telefone / WhatsApp')}
            </label>
            <Input
              type="tel"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              placeholder="+55 11 99999-9999"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              {t('onboarding.contact.emailLabel', 'Email (opcional)')}
            </label>
            <Input
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="contato@seunegocio.com.br"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              {t('onboarding.contact.addressLabel', 'Endereço (opcional)')}
            </label>
            <Input
              value={contactAddress}
              onChange={(e) => setContactAddress(e.target.value)}
              placeholder={t('onboarding.contact.addressPlaceholder', 'Rua, número, bairro, cidade')}
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-center gap-3">
        <Button variant="outline" onClick={onBack} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          {t('common.back', 'Voltar')}
        </Button>
        <Button
          onClick={onNext}
          disabled={isSubmitting}
          size="lg"
          className="gap-2 bg-[hsl(160,60%,18%)] hover:bg-[hsl(160,60%,22%)] text-white px-8"
        >
          {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {t('common.next', 'Próximo')}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function ServicesStep({
  services,
  setServices,
  onNext,
  onBack,
  isSubmitting,
}: {
  services: ServiceEntry[];
  setServices: (v: ServiceEntry[]) => void;
  onNext: () => void;
  onBack: () => void;
  isSubmitting: boolean;
}) {
  const { t } = useTranslation();

  const addService = () => {
    setServices([
      ...services,
      { name: '', description: '', durationMinutes: 30, priceCents: 5000 },
    ]);
  };

  const removeService = (index: number) => {
    setServices(services.filter((_, i) => i !== index));
  };

  const updateService = (index: number, field: keyof ServiceEntry, value: string | number) => {
    const updated = services.map((s, i) => {
      if (i !== index) return s;
      return { ...s, [field]: value };
    });
    setServices(updated);
  };

  const hasValidServices = services.length === 0 || services.some((s) => s.name.trim().length > 0);

  return (
    <div className="space-y-6">
      <div className="text-center max-w-lg mx-auto">
        <div className="w-14 h-14 rounded-2xl bg-[hsl(160,84%,39%)]/10 flex items-center justify-center mx-auto mb-4">
          <Scissors className="h-7 w-7 text-[hsl(160,60%,18%)]" />
        </div>
        <h2 className="text-xl font-bold text-foreground mb-2">
          {t('onboarding.services.title', 'Serviços')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('onboarding.services.description', 'Adicione os serviços que você oferece. Pode pular e adicionar depois.')}
        </p>
      </div>

      <div className="max-w-2xl mx-auto space-y-3">
        {services.map((service, index) => (
          <Card key={index}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-start gap-3">
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-4 gap-3">
                  <div className="sm:col-span-2">
                    <Input
                      value={service.name}
                      onChange={(e) => updateService(index, 'name', e.target.value)}
                      placeholder={t('onboarding.services.namePlaceholder', 'Nome do serviço')}
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Input
                      type="number"
                      value={service.durationMinutes}
                      onChange={(e) => updateService(index, 'durationMinutes', parseInt(e.target.value) || 0)}
                      placeholder="30"
                      className="text-sm"
                    />
                    <span className="text-xs text-muted-foreground">min</span>
                  </div>
                  <div>
                    <Input
                      type="number"
                      value={(service.priceCents / 100).toFixed(2)}
                      onChange={(e) => updateService(index, 'priceCents', Math.round(parseFloat(e.target.value) * 100) || 0)}
                      placeholder="50.00"
                      className="text-sm"
                    />
                    <span className="text-xs text-muted-foreground">R$</span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeService(index)}
                  className="text-muted-foreground hover:text-destructive shrink-0 mt-1"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        <Button
          variant="outline"
          onClick={addService}
          className="w-full gap-2 border-dashed"
        >
          <Plus className="h-4 w-4" />
          {t('onboarding.services.addService', 'Adicionar Serviço')}
        </Button>

        {services.length === 0 && (
          <p className="text-center text-sm text-muted-foreground">
            {t('onboarding.services.skipHint', 'Você pode pular esta etapa e adicionar serviços depois.')}
          </p>
        )}
      </div>

      <div className="flex justify-center gap-3">
        <Button variant="outline" onClick={onBack} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          {t('common.back', 'Voltar')}
        </Button>
        <Button
          onClick={onNext}
          disabled={!hasValidServices || isSubmitting}
          size="lg"
          className="gap-2 bg-[hsl(160,60%,18%)] hover:bg-[hsl(160,60%,22%)] text-white px-8"
        >
          {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {services.length === 0
            ? t('common.skip', 'Pular')
            : t('common.next', 'Próximo')}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function BusinessHoursStep({
  hours,
  setHours,
  onNext,
  onBack,
  isSubmitting,
}: {
  hours: Record<string, DayHours>;
  setHours: (v: Record<string, DayHours>) => void;
  onNext: () => void;
  onBack: () => void;
  isSubmitting: boolean;
}) {
  const { t } = useTranslation();

  const toggleDay = (day: string) => {
    setHours({
      ...hours,
      [day]: { ...hours[day], enabled: !hours[day].enabled },
    });
  };

  const updateHour = (day: string, field: 'open' | 'close', value: string) => {
    setHours({
      ...hours,
      [day]: { ...hours[day], [field]: value },
    });
  };

  return (
    <div className="space-y-6">
      <div className="text-center max-w-lg mx-auto">
        <div className="w-14 h-14 rounded-2xl bg-[hsl(160,84%,39%)]/10 flex items-center justify-center mx-auto mb-4">
          <Clock className="h-7 w-7 text-[hsl(160,60%,18%)]" />
        </div>
        <h2 className="text-xl font-bold text-foreground mb-2">
          {t('onboarding.hours.title', 'Horário de Funcionamento')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('onboarding.hours.description', 'Defina seus horários de atendimento para cada dia da semana.')}
        </p>
      </div>

      <Card className="max-w-lg mx-auto">
        <CardContent className="pt-6 space-y-3">
          {DAY_KEYS.map((day) => {
            const dayHours = hours[day];
            return (
              <div key={day} className="flex items-center gap-3">
                <button
                  onClick={() => toggleDay(day)}
                  className={`w-24 text-left text-sm font-medium transition-colors ${
                    dayHours.enabled ? 'text-foreground' : 'text-muted-foreground line-through'
                  }`}
                >
                  {t(`onboarding.hours.days.${day}`, day)}
                </button>
                {dayHours.enabled ? (
                  <div className="flex items-center gap-2 flex-1">
                    <Input
                      type="time"
                      value={dayHours.open}
                      onChange={(e) => updateHour(day, 'open', e.target.value)}
                      className="w-[120px] text-sm"
                    />
                    <span className="text-muted-foreground text-sm">-</span>
                    <Input
                      type="time"
                      value={dayHours.close}
                      onChange={(e) => updateHour(day, 'close', e.target.value)}
                      className="w-[120px] text-sm"
                    />
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground italic flex-1">
                    {t('onboarding.hours.closed', 'Fechado')}
                  </span>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleDay(day)}
                  className={dayHours.enabled ? 'text-[hsl(160,84%,39%)]' : 'text-muted-foreground'}
                >
                  {dayHours.enabled
                    ? <CheckCircle2 className="h-4 w-4" />
                    : <X className="h-4 w-4" />}
                </Button>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <div className="flex justify-center gap-3">
        <Button variant="outline" onClick={onBack} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          {t('common.back', 'Voltar')}
        </Button>
        <Button
          onClick={onNext}
          disabled={isSubmitting}
          size="lg"
          className="gap-2 bg-[hsl(160,60%,18%)] hover:bg-[hsl(160,60%,22%)] text-white px-8"
        >
          {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {t('common.next', 'Próximo')}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function StaffSetupStep({
  staffMembers,
  setStaffMembers,
  editingStaffIndex,
  setEditingStaffIndex,
  services,
  hours,
  businessName,
  isLoadingStaff,
  onNext,
  onBack,
  isSubmitting,
}: {
  staffMembers: StaffMember[];
  setStaffMembers: (v: StaffMember[]) => void;
  editingStaffIndex: number | null;
  setEditingStaffIndex: (v: number | null) => void;
  services: ServiceEntry[];
  hours: Record<string, DayHours>;
  businessName: string;
  isLoadingStaff: boolean;
  onNext: () => void;
  onBack: () => void;
  isSubmitting: boolean;
}) {
  const { t } = useTranslation();

  const buildDefaultAvailability = (): StaffMember['availability'] => {
    const dayMap: Record<string, number> = {
      sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
      thursday: 4, friday: 5, saturday: 6,
    };
    return Object.entries(hours).map(([day, h]) => ({
      dayOfWeek: dayMap[day] ?? 0,
      isAvailable: h.enabled,
      slots: h.enabled ? [{ start: h.open, end: h.close }] : [],
    }));
  };

  const handleSoloSetup = () => {
    const allServiceIds = services.filter(s => s.id).map(s => s.id!);
    const owner: StaffMember = {
      name: businessName || 'Owner',
      role: 'OWNER',
      availability: buildDefaultAvailability(),
      serviceIds: allServiceIds,
      isNew: true,
    };
    setStaffMembers([owner]);
    setEditingStaffIndex(null);
  };

  const addNewMember = () => {
    const newMember: StaffMember = {
      name: '',
      role: 'STAFF',
      availability: buildDefaultAvailability(),
      serviceIds: [],
      isNew: true,
    };
    setStaffMembers([...staffMembers, newMember]);
    setEditingStaffIndex(staffMembers.length);
  };

  const removeMember = (index: number) => {
    if (!window.confirm(t('onboarding.teamSetup.removeConfirm', 'Remove this team member?'))) return;
    setStaffMembers(staffMembers.filter((_, i) => i !== index));
    if (editingStaffIndex === index) setEditingStaffIndex(null);
    else if (editingStaffIndex !== null && editingStaffIndex > index) {
      setEditingStaffIndex(editingStaffIndex - 1);
    }
  };

  const updateMember = (index: number, updates: Partial<StaffMember>) => {
    setStaffMembers(staffMembers.map((m, i) => i === index ? { ...m, ...updates } : m));
  };

  const toggleService = (memberIndex: number, serviceId: string) => {
    const member = staffMembers[memberIndex];
    const ids = member.serviceIds.includes(serviceId)
      ? member.serviceIds.filter(id => id !== serviceId)
      : [...member.serviceIds, serviceId];
    updateMember(memberIndex, { serviceIds: ids });
  };

  const toggleAvailDay = (memberIndex: number, dayOfWeek: number) => {
    const member = staffMembers[memberIndex];
    const avail = member.availability.map(a =>
      a.dayOfWeek === dayOfWeek
        ? { ...a, isAvailable: !a.isAvailable, slots: !a.isAvailable ? [{ start: '09:00', end: '18:00' }] : [] }
        : a,
    );
    updateMember(memberIndex, { availability: avail });
  };

  const updateAvailSlot = (memberIndex: number, dayOfWeek: number, field: 'start' | 'end', value: string) => {
    const member = staffMembers[memberIndex];
    const avail = member.availability.map(a =>
      a.dayOfWeek === dayOfWeek
        ? { ...a, slots: a.slots.map((s, si) => si === 0 ? { ...s, [field]: value } : s) }
        : a,
    );
    updateMember(memberIndex, { availability: avail });
  };

  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const canContinue = staffMembers.length === 0 || staffMembers.every(m => m.name.trim().length > 0);

  if (isLoadingStaff) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center max-w-lg mx-auto">
        <div className="w-14 h-14 rounded-2xl bg-[hsl(160,84%,39%)]/10 flex items-center justify-center mx-auto mb-4">
          <Users className="h-7 w-7 text-[hsl(160,60%,18%)]" />
        </div>
        <h2 className="text-xl font-bold text-foreground mb-2">
          {t('onboarding.teamSetup.title', 'Configuração da Equipe')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('onboarding.teamSetup.description', 'Defina quem vai atender os agendamentos.')}
        </p>
      </div>

      {staffMembers.length === 0 ? (
        /* Empty state: Solo setup or add member */
        <div className="max-w-lg mx-auto space-y-4">
          <Card className="border-[hsl(160,84%,39%)]/30">
            <CardHeader>
              <CardTitle className="text-base">
                {t('onboarding.teamSetup.soloTitle', 'Configuração Rápida - Sou Eu Mesmo')}
              </CardTitle>
              <CardDescription>
                {t('onboarding.teamSetup.soloDescription', 'Se é só você, criaremos seu perfil com os dados do negócio.')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-[hsl(160,84%,39%)]" />
                  <span>{t('onboarding.teamSetup.soloBenefit1', 'Seu calendário mostrará sua disponibilidade')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-[hsl(160,84%,39%)]" />
                  <span>{t('onboarding.teamSetup.soloBenefit2', 'Agendamentos serão atribuídos a você')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-[hsl(160,84%,39%)]" />
                  <span>{t('onboarding.teamSetup.soloBenefit3', 'Você pode adicionar membros depois')}</span>
                </div>
              </div>
              <Button
                onClick={handleSoloSetup}
                className="w-full gap-2 bg-[hsl(160,60%,18%)] hover:bg-[hsl(160,60%,22%)] text-white"
              >
                <Users className="h-4 w-4" />
                {t('onboarding.teamSetup.soloButton', 'Sou eu mesmo')}
              </Button>
            </CardContent>
          </Card>

          <div className="text-center text-sm text-muted-foreground">
            {t('common.or', 'ou')}
          </div>

          <Button variant="outline" onClick={addNewMember} className="w-full gap-2 border-dashed">
            <Plus className="h-4 w-4" />
            {t('onboarding.teamSetup.addMember', 'Adicionar Membro')}
          </Button>
        </div>
      ) : (
        /* Staff list */
        <div className="max-w-2xl mx-auto space-y-3">
          <h3 className="text-sm font-medium text-muted-foreground">
            {t('onboarding.teamSetup.listTitle', 'Membros da Equipe ({{count}})', { count: staffMembers.length })}
          </h3>

          {staffMembers.map((member, index) => (
            <Card key={index} className={editingStaffIndex === index ? 'ring-2 ring-[hsl(160,84%,39%)]/30' : ''}>
              <CardContent className="pt-4 pb-4">
                {editingStaffIndex === index ? (
                  /* Inline editor */
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <label className="text-xs font-medium">{t('onboarding.teamSetup.nameLabel', 'Nome')}</label>
                        <Input
                          value={member.name}
                          onChange={e => updateMember(index, { name: e.target.value })}
                          placeholder={t('onboarding.teamSetup.namePlaceholder', 'Nome do membro')}
                          className="text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium">{t('onboarding.teamSetup.emailLabel', 'Email')}</label>
                        <Input
                          type="email"
                          value={member.email || ''}
                          onChange={e => updateMember(index, { email: e.target.value || undefined })}
                          placeholder={t('onboarding.teamSetup.emailPlaceholder', 'email@exemplo.com')}
                          className="text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium">{t('onboarding.teamSetup.phoneLabel', 'Telefone')}</label>
                        <Input
                          type="tel"
                          value={member.phone || ''}
                          onChange={e => updateMember(index, { phone: e.target.value || undefined })}
                          placeholder={t('onboarding.teamSetup.phonePlaceholder', '+55 11 99999-9999')}
                          className="text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium">{t('onboarding.teamSetup.roleLabel', 'Função')}</label>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant={member.role === 'OWNER' ? 'default' : 'outline'}
                            onClick={() => updateMember(index, { role: 'OWNER' })}
                            className={member.role === 'OWNER' ? 'bg-[hsl(160,60%,18%)] text-white' : ''}
                          >
                            {t('onboarding.teamSetup.roleOwner', 'Proprietário')}
                          </Button>
                          <Button
                            size="sm"
                            variant={member.role === 'STAFF' ? 'default' : 'outline'}
                            onClick={() => updateMember(index, { role: 'STAFF' })}
                            className={member.role === 'STAFF' ? 'bg-[hsl(160,60%,18%)] text-white' : ''}
                          >
                            {t('onboarding.teamSetup.roleStaff', 'Funcionário')}
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Service assignment */}
                    {services.filter(s => s.id).length > 0 && (
                      <div className="space-y-2">
                        <label className="text-xs font-medium">{t('onboarding.teamSetup.servicesLabel', 'Serviços')}</label>
                        <div className="flex flex-wrap gap-2">
                          {services.filter(s => s.id).map(s => (
                            <button
                              key={s.id}
                              onClick={() => toggleService(index, s.id!)}
                              className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                                member.serviceIds.includes(s.id!)
                                  ? 'bg-[hsl(160,84%,39%)]/10 border-[hsl(160,84%,39%)] text-[hsl(160,60%,18%)]'
                                  : 'border-border text-muted-foreground hover:border-[hsl(160,60%,18%)]/30'
                              }`}
                            >
                              {s.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Availability grid */}
                    <div className="space-y-2">
                      <label className="text-xs font-medium">{t('onboarding.teamSetup.availabilityLabel', 'Disponibilidade')}</label>
                      <div className="space-y-1.5">
                        {member.availability
                          .sort((a, b) => a.dayOfWeek - b.dayOfWeek)
                          .map(avail => (
                          <div key={avail.dayOfWeek} className="flex items-center gap-2">
                            <button
                              onClick={() => toggleAvailDay(index, avail.dayOfWeek)}
                              className={`w-12 text-left text-xs font-medium transition-colors ${
                                avail.isAvailable ? 'text-foreground' : 'text-muted-foreground line-through'
                              }`}
                            >
                              {dayLabels[avail.dayOfWeek]}
                            </button>
                            {avail.isAvailable ? (
                              <div className="flex items-center gap-1 flex-1">
                                <Input
                                  type="time"
                                  value={avail.slots[0]?.start || '09:00'}
                                  onChange={e => updateAvailSlot(index, avail.dayOfWeek, 'start', e.target.value)}
                                  className="w-[100px] h-7 text-xs"
                                />
                                <span className="text-xs text-muted-foreground">-</span>
                                <Input
                                  type="time"
                                  value={avail.slots[0]?.end || '18:00'}
                                  onChange={e => updateAvailSlot(index, avail.dayOfWeek, 'end', e.target.value)}
                                  className="w-[100px] h-7 text-xs"
                                />
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground italic flex-1">
                                {t('onboarding.hours.closed', 'Fechado')}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    <Button size="sm" variant="outline" onClick={() => setEditingStaffIndex(null)}>
                      {t('common.close', 'Fechar')}
                    </Button>
                  </div>
                ) : (
                  /* Summary row */
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-[hsl(160,84%,39%)]/10 flex items-center justify-center shrink-0">
                      <Users className="h-4 w-4 text-[hsl(160,60%,18%)]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate">{member.name || '(no name)'}</span>
                        <Badge variant="outline" className="text-[10px] shrink-0">
                          {member.role === 'OWNER'
                            ? t('onboarding.teamSetup.roleOwner', 'Proprietário')
                            : t('onboarding.teamSetup.roleStaff', 'Funcionário')}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {member.serviceIds.length} {t('onboarding.teamSetup.list.services', 'services')}
                        {' · '}
                        {member.availability.filter(a => a.isAvailable).length} {t('onboarding.teamSetup.list.days', 'days')}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="sm" variant="ghost" onClick={() => setEditingStaffIndex(index)}>
                        {t('onboarding.teamSetup.editButton', 'Editar')}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeMember(index)}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}

          <Button variant="outline" onClick={addNewMember} className="w-full gap-2 border-dashed">
            <Plus className="h-4 w-4" />
            {t('onboarding.teamSetup.addAnother', 'Adicionar Outro Membro')}
          </Button>
        </div>
      )}

      <div className="flex justify-center gap-3">
        <Button variant="outline" onClick={onBack} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          {t('common.back', 'Voltar')}
        </Button>
        <Button
          onClick={onNext}
          disabled={!canContinue || isSubmitting}
          size="lg"
          className="gap-2 bg-[hsl(160,60%,18%)] hover:bg-[hsl(160,60%,22%)] text-white px-8"
        >
          {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {staffMembers.length === 0
            ? t('common.skip', 'Pular')
            : t('common.next', 'Próximo')}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function WhatsAppSetupStep({
  onNext,
  onBack,
}: {
  onNext: () => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <div className="text-center max-w-lg mx-auto">
        <div className="w-14 h-14 rounded-2xl bg-[hsl(160,84%,39%)]/10 flex items-center justify-center mx-auto mb-4">
          <MessageSquare className="h-7 w-7 text-[hsl(160,60%,18%)]" />
        </div>
        <h2 className="text-xl font-bold text-foreground mb-2">
          {t('onboarding.whatsapp.title', 'WhatsApp Business')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('onboarding.whatsapp.description', 'A integração com WhatsApp Business requer configuração adicional com o Meta Business.')}
        </p>
      </div>

      <Card className="max-w-lg mx-auto border-amber-200 bg-amber-50/50">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-500" />
            {t('onboarding.whatsapp.devModeTitle', 'Modo Desenvolvimento')}
          </CardTitle>
          <CardDescription>
            {t(
              'onboarding.whatsapp.devModeDesc',
              'A conexão com WhatsApp Business API (Meta) está disponível apenas em ambiente de produção. Por enquanto, o sistema operará com funcionalidades de demonstração.',
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3 p-3 rounded-lg bg-white border">
            <CheckCircle2 className="h-5 w-5 text-[hsl(160,84%,39%)]" />
            <span className="text-sm">{t('onboarding.whatsapp.feature1', 'Gerenciamento de conversas')}</span>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-lg bg-white border">
            <CheckCircle2 className="h-5 w-5 text-[hsl(160,84%,39%)]" />
            <span className="text-sm">{t('onboarding.whatsapp.feature2', 'Templates de mensagem')}</span>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-lg bg-white border">
            <CheckCircle2 className="h-5 w-5 text-[hsl(160,84%,39%)]" />
            <span className="text-sm">{t('onboarding.whatsapp.feature3', 'Fluxos de atendimento')}</span>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-center gap-3">
        <Button variant="outline" onClick={onBack} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          {t('common.back', 'Voltar')}
        </Button>
        <Button
          onClick={onNext}
          size="lg"
          className="gap-2 bg-[hsl(160,60%,18%)] hover:bg-[hsl(160,60%,22%)] text-white px-8"
        >
          {t('common.next', 'Próximo')}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function TemplatesStep({
  templates,
  isLoadingTemplates,
  onNext,
  onBack,
  isSubmitting,
}: {
  templates: TemplateEntry[];
  isLoadingTemplates: boolean;
  onNext: () => void;
  onBack: () => void;
  isSubmitting: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <div className="text-center max-w-lg mx-auto">
        <div className="w-14 h-14 rounded-2xl bg-[hsl(160,84%,39%)]/10 flex items-center justify-center mx-auto mb-4">
          <FileText className="h-7 w-7 text-[hsl(160,60%,18%)]" />
        </div>
        <h2 className="text-xl font-bold text-foreground mb-2">
          {t('onboarding.templates.title', 'Templates de Mensagem')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('onboarding.templates.description', 'Templates serão criados automaticamente na finalização. Você poderá editá-los depois.')}
        </p>
      </div>

      <Card className="max-w-lg mx-auto">
        <CardContent className="pt-6">
          {isLoadingTemplates ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : templates.length > 0 ? (
            <div className="space-y-3">
              {templates.map((tmpl) => (
                <div key={tmpl.id} className="flex items-start gap-3 p-3 rounded-lg border">
                  <FileText className="h-5 w-5 text-[hsl(160,84%,39%)] shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{tmpl.slug}</p>
                    <p className="text-xs text-muted-foreground line-clamp-2">{tmpl.bodyText}</p>
                  </div>
                  <Badge variant="outline" className="text-xs shrink-0">
                    {tmpl.isActive ? t('common.active', 'Ativo') : t('common.inactive', 'Inativo')}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Sparkles className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                {t('onboarding.templates.willBeCreated', 'Templates serão criados automaticamente ao finalizar o onboarding.')}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-center gap-3">
        <Button variant="outline" onClick={onBack} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          {t('common.back', 'Voltar')}
        </Button>
        <Button
          onClick={onNext}
          disabled={isSubmitting}
          size="lg"
          className="gap-2 bg-[hsl(160,60%,18%)] hover:bg-[hsl(160,60%,22%)] text-white px-8"
        >
          {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          <Zap className="h-4 w-4" />
          {t('onboarding.templates.finalize', 'Finalizar Configuração')}
        </Button>
      </div>
    </div>
  );
}

function CompleteStep({
  businessName,
  servicesCount,
  hoursConfigured,
  staffCount,
  templatesCount,
  onGoToDashboard,
}: {
  businessName: string;
  servicesCount: number;
  hoursConfigured: boolean;
  staffCount: number;
  templatesCount: number;
  onGoToDashboard: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="space-y-8">
      <div className="text-center max-w-md mx-auto">
        <div className="w-20 h-20 rounded-full bg-[hsl(160,84%,39%)]/10 flex items-center justify-center mx-auto mb-4 animate-in zoom-in duration-500">
          <PartyPopper className="h-10 w-10 text-[hsl(160,84%,39%)]" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-2">
          {t('onboarding.complete.title', 'Tudo Pronto!')}
        </h2>
        <p className="text-muted-foreground">
          {t(
            'onboarding.complete.description',
            'Seu negócio está configurado e pronto para uso. Veja o resumo:',
          )}
        </p>
      </div>

      <Card className="max-w-md mx-auto border-[hsl(160,84%,39%)]/20">
        <CardContent className="pt-6">
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-[hsl(160,84%,39%)]/5">
              <CheckCircle2 className="h-5 w-5 text-[hsl(160,84%,39%)]" />
              <span className="text-sm font-medium text-foreground">
                {t('onboarding.complete.profileSet', 'Perfil configurado: {{name}}', { name: businessName })}
              </span>
            </div>
            {servicesCount > 0 && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-[hsl(160,84%,39%)]/5">
                <CheckCircle2 className="h-5 w-5 text-[hsl(160,84%,39%)]" />
                <span className="text-sm font-medium text-foreground">
                  {t('onboarding.complete.servicesCreated', '{{count}} serviços criados', { count: servicesCount })}
                </span>
              </div>
            )}
            {hoursConfigured && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-[hsl(160,84%,39%)]/5">
                <CheckCircle2 className="h-5 w-5 text-[hsl(160,84%,39%)]" />
                <span className="text-sm font-medium text-foreground">
                  {t('onboarding.complete.hoursSet', 'Horários de funcionamento definidos')}
                </span>
              </div>
            )}
            {staffCount > 0 && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-[hsl(160,84%,39%)]/5">
                <CheckCircle2 className="h-5 w-5 text-[hsl(160,84%,39%)]" />
                <span className="text-sm font-medium text-foreground">
                  {t('onboarding.complete.staffConfigured', '{{count}} membros da equipe configurados', { count: staffCount })}
                </span>
              </div>
            )}
            {templatesCount > 0 && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-[hsl(160,84%,39%)]/5">
                <CheckCircle2 className="h-5 w-5 text-[hsl(160,84%,39%)]" />
                <span className="text-sm font-medium text-foreground">
                  {t('onboarding.complete.templatesCreated', '{{count}} templates criados', { count: templatesCount })}
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col items-center gap-3">
        <Button
          onClick={onGoToDashboard}
          size="lg"
          className="gap-2 bg-[hsl(160,60%,18%)] hover:bg-[hsl(160,60%,22%)] text-white shadow-lg px-8"
        >
          {t('onboarding.complete.goToDashboard', 'Ir para o Painel')}
          <ArrowRight className="h-4 w-4" />
        </Button>
        <p className="text-xs text-muted-foreground">
          {t(
            'onboarding.complete.hint',
            'Você pode editar todas as configurações a qualquer momento no menu Configurações.',
          )}
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// Main Page
// ============================================================================

function OnboardingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { operator, isLoading: authLoading, selectedBusinessId, selectedBusiness } = useOperator();

  // Wizard state
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState<WizardStep>('BUSINESS_PROFILE');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Step data
  const [businessName, setBusinessName] = useState('');
  const [businessDescription, setBusinessDescription] = useState('');
  const [businessCategory, setBusinessCategory] = useState<BusinessType>('BARBERSHOP');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactAddress, setContactAddress] = useState('');
  const [services, setServices] = useState<ServiceEntry[]>([]);
  const [hours, setHours] = useState<Record<string, DayHours>>({ ...DEFAULT_HOURS });
  const [templates, setTemplates] = useState<TemplateEntry[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

  // Staff state
  const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);
  const [editingStaffIndex, setEditingStaffIndex] = useState<number | null>(null);
  const [isLoadingStaff, setIsLoadingStaff] = useState(false);

  // Completion summary
  const [completionServicesCount, setCompletionServicesCount] = useState(0);
  const [completionTemplatesCount, setCompletionTemplatesCount] = useState(0);

  // Load initial state
  const initialize = useCallback(async () => {
    if (!selectedBusinessId) return;
    setLoading(true);
    setError(null);

    try {
      // Get onboarding status
      const status = await onboardingApi.getStatus(selectedBusinessId);

      if (status?.status === 'COMPLETED') {
        // Already completed - go to complete step
        setCurrentStep('COMPLETE');
        setSessionId(status.sessionId);
        setLoading(false);
        return;
      }

      if (status?.sessionId) {
        setSessionId(status.sessionId);
      }

      // Load existing business data to populate form
      if (selectedBusiness) {
        setBusinessName(selectedBusiness.name || '');
      }

      // Load existing services
      const existingServices = await onboardingApi.getServices(selectedBusinessId);
      if (existingServices.length > 0) {
        setServices(
          existingServices.map((s: Record<string, unknown>) => ({
            id: s.id as string,
            name: s.name as string,
            description: (s.description as string) || '',
            durationMinutes: s.durationMinutes as number,
            priceCents: s.priceCents as number,
          })),
        );
      }

      // Load existing hours
      const existingHours = await onboardingApi.getBusinessHours(selectedBusinessId);
      if (existingHours) {
        const dayOrder = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const loaded: Record<string, DayHours> = { ...DEFAULT_HOURS };
        for (const day of dayOrder) {
          if (existingHours[day]) {
            loaded[day] = {
              open: existingHours[day].open,
              close: existingHours[day].close,
              enabled: true,
            };
          } else {
            loaded[day] = { ...loaded[day], enabled: false };
          }
        }
        setHours(loaded);
      }

      // Resume at the right step based on stepData
      if (status?.stepData && typeof status.stepData === 'object') {
        const stepData = status.stepData as Record<string, unknown>;
        const wizardStep = stepData.wizardStep as number | undefined;
        if (wizardStep && wizardStep >= 1 && wizardStep <= 8) {
          const stepIndex = Math.min(wizardStep, STEPS.length - 1);
          setCurrentStep(STEPS[stepIndex] || 'BUSINESS_PROFILE');
        }
      }
    } catch (err) {
      console.warn('Onboarding initialization:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedBusinessId, selectedBusiness]);

  useEffect(() => {
    if (!operator || !selectedBusinessId) return;
    const controller = new AbortController();
    initialize().catch(() => {});
    return () => controller.abort();
  }, [operator, selectedBusinessId, initialize]);

  // Load templates when reaching templates step
  useEffect(() => {
    if (currentStep !== 'TEMPLATES' || !selectedBusinessId) return;
    const controller = new AbortController();
    setIsLoadingTemplates(true);
    onboardingApi.getTemplates(selectedBusinessId).then((data) => {
      if (controller.signal.aborted) return;
      setTemplates(
        (data || []).map((t: Record<string, unknown>) => ({
          id: t.id as string,
          slug: t.slug as string,
          bodyText: t.bodyText as string,
          isActive: t.isActive as boolean,
        })),
      );
      setIsLoadingTemplates(false);
    }).catch(() => {
      if (!controller.signal.aborted) setIsLoadingTemplates(false);
    });
    return () => controller.abort();
  }, [currentStep, selectedBusinessId]);

  // Load staff when reaching team setup step
  useEffect(() => {
    if (currentStep !== 'TEAM_SETUP' || !selectedBusinessId || staffMembers.length > 0) return;
    const controller = new AbortController();
    setIsLoadingStaff(true);
    apiFetch(`/api/operator/staff?businessId=${encodeURIComponent(selectedBusinessId)}&active=true`, { signal: controller.signal })
      .then(async (res) => {
        if (controller.signal.aborted) return;
        if (!res.ok) { setIsLoadingStaff(false); return; }
        const json = await res.json();
        const data = json.data || [];
        if (data.length > 0) {
          const members: StaffMember[] = await Promise.all(
            data.map(async (s: Record<string, unknown>) => {
              // Fetch availability and services for each staff
              let availability: StaffMember['availability'] = [];
              let serviceIds: string[] = [];
              try {
                const availRes = await apiFetch(`/api/operator/staff/${s.id}/availability`, { signal: controller.signal });
                if (!controller.signal.aborted && availRes.ok) {
                  const availJson = await availRes.json();
                  const schedule = availJson.data?.schedule || availJson.data || [];
                  availability = (Array.isArray(schedule) ? schedule : []).map((a: Record<string, unknown>) => ({
                    dayOfWeek: a.dayOfWeek as number,
                    isAvailable: a.isAvailable as boolean,
                    slots: a.slots as { start: string; end: string }[],
                  }));
                }
              } catch { /* use empty */ }
              try {
                const svcRes = await apiFetch(`/api/operator/staff/${s.id}/services`, { signal: controller.signal });
                if (!controller.signal.aborted && svcRes.ok) {
                  const svcJson = await svcRes.json();
                  const assigned = svcJson.data?.assigned || svcJson.data || [];
                  serviceIds = (Array.isArray(assigned) ? assigned : []).map((sv: Record<string, unknown>) => sv.id as string);
                }
              } catch { /* use empty */ }
              return {
                id: s.id as string,
                name: s.name as string,
                email: s.email as string | undefined,
                phone: s.phone as string | undefined,
                role: (s.role as 'OWNER' | 'STAFF') || 'STAFF',
                color: s.color as string | undefined,
                availability,
                serviceIds,
                isNew: false,
              };
            }),
          );
          if (!controller.signal.aborted) setStaffMembers(members);
        }
        if (!controller.signal.aborted) setIsLoadingStaff(false);
      })
      .catch((err) => {
        if (err instanceof Error && err.name === 'AbortError') return;
        if (!controller.signal.aborted) setIsLoadingStaff(false);
      });
    return () => controller.abort();
  }, [currentStep, selectedBusinessId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Step navigation with data saving
  const goToStep = useCallback((step: WizardStep) => {
    setCurrentStep(step);
    setError(null);
  }, []);

  const handleBusinessProfileNext = useCallback(async () => {
    if (!selectedBusinessId) return;
    setIsSubmitting(true);
    setError(null);

    try {
      // Start session if needed
      if (!sessionId) {
        const result = await onboardingApi.startSession(selectedBusinessId);
        setSessionId(result.sessionId);
      }

      // Update business name
      await onboardingApi.updateBusinessProfile(selectedBusinessId, {
        name: businessName.trim(),
      });

      // Save step progress
      await onboardingApi.saveStep(selectedBusinessId, 1, {
        businessName: businessName.trim(),
        businessDescription,
        businessCategory,
      });

      goToStep('CONTACT_INFO');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('onboarding.errors.saveFailed', 'Falha ao salvar dados'),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedBusinessId, sessionId, businessName, businessDescription, businessCategory, goToStep, t]);

  const handleContactInfoNext = useCallback(async () => {
    if (!selectedBusinessId) return;
    setIsSubmitting(true);
    setError(null);

    try {
      // Save step progress with contact info
      await onboardingApi.saveStep(selectedBusinessId, 2, {
        contactPhone,
        contactEmail,
        contactAddress,
      });

      goToStep('SERVICES');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('onboarding.errors.saveFailed', 'Falha ao salvar dados'),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedBusinessId, contactPhone, contactEmail, contactAddress, goToStep, t]);

  const handleServicesNext = useCallback(async () => {
    if (!selectedBusinessId) return;
    setIsSubmitting(true);
    setError(null);

    try {
      // Create services via the services API
      const validServices = services.filter((s) => s.name.trim().length > 0 && !s.id);
      for (const service of validServices) {
        await onboardingApi.createService(selectedBusinessId, service);
      }

      // Save step progress
      await onboardingApi.saveStep(selectedBusinessId, 3, {
        servicesCount: services.filter((s) => s.name.trim().length > 0).length,
      });

      goToStep('BUSINESS_HOURS');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('onboarding.errors.saveFailed', 'Falha ao salvar dados'),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedBusinessId, services, goToStep, t]);

  const handleBusinessHoursNext = useCallback(async () => {
    if (!selectedBusinessId) return;
    setIsSubmitting(true);
    setError(null);

    try {
      // Transform hours to API format
      const apiHours: Record<string, { open: string; close: string } | null> = {};
      for (const [day, dayHours] of Object.entries(hours)) {
        apiHours[day] = dayHours.enabled
          ? { open: dayHours.open, close: dayHours.close }
          : null;
      }

      await onboardingApi.saveBusinessHours(selectedBusinessId, apiHours);

      // Save step progress
      await onboardingApi.saveStep(selectedBusinessId, 4, {
        hoursConfigured: true,
      });

      goToStep('TEAM_SETUP');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('onboarding.errors.saveFailed', 'Falha ao salvar dados'),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedBusinessId, hours, goToStep, t]);

  const handleTeamSetupNext = useCallback(async () => {
    if (!selectedBusinessId) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const staffIds: string[] = [];

      for (const member of staffMembers) {
        let staffId = member.id;

        if (member.isNew) {
          // Create staff member
          const createRes = await apiFetch('/api/operator/staff', {
            method: 'POST',
            body: JSON.stringify({
              businessId: selectedBusinessId,
              name: member.name,
              email: member.email || undefined,
              phone: member.phone || undefined,
              role: member.role,
              color: member.color || undefined,
            }),
          });
          if (!createRes.ok) throw new Error('CREATE_STAFF_FAILED');
          const createJson = await createRes.json();
          staffId = createJson.data?.id;
        }

        if (!staffId) continue;
        staffIds.push(staffId);

        // Set availability
        if (member.availability.length > 0) {
          await apiFetch(`/api/operator/staff/${staffId}/availability`, {
            method: 'PUT',
            body: JSON.stringify({
              schedule: member.availability.map(a => ({
                dayOfWeek: a.dayOfWeek,
                isAvailable: a.isAvailable,
                slots: a.slots,
              })),
            }),
          });
        }

        // Assign services
        if (member.serviceIds.length > 0) {
          await apiFetch(`/api/operator/staff/${staffId}/services`, {
            method: 'PUT',
            body: JSON.stringify({ serviceIds: member.serviceIds }),
          });
        }
      }

      // Save step progress
      await onboardingApi.saveStep(selectedBusinessId, 5, {
        staffCreated: staffMembers.length > 0,
        staffCount: staffMembers.length,
        staffIds,
      });

      goToStep('WHATSAPP_SETUP');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('onboarding.teamSetup.errors.saveFailed', 'Falha ao salvar dados da equipe'),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedBusinessId, staffMembers, goToStep, t]);

  const handleWhatsAppNext = useCallback(async () => {
    if (!selectedBusinessId) return;

    try {
      // Save step progress (WhatsApp skipped in dev mode)
      await onboardingApi.saveStep(selectedBusinessId, 6, {
        whatsappSkipped: true,
        devMode: true,
      });
    } catch {
      // Non-blocking
    }

    goToStep('TEMPLATES');
  }, [selectedBusinessId, goToStep]);

  const handleTemplatesNext = useCallback(async () => {
    if (!selectedBusinessId) return;
    setIsSubmitting(true);
    setError(null);

    try {
      // Call setup-defaults to create any remaining defaults
      const result = await onboardingApi.setupDefaults(selectedBusinessId, businessCategory);

      const created = result?.created || {};
      setCompletionServicesCount(
        (services.filter((s) => s.name.trim().length > 0).length) + (created.services || 0),
      );
      setCompletionTemplatesCount(created.messageTemplates || 0);

      // Save step progress
      await onboardingApi.saveStep(selectedBusinessId, 7, {
        setupDefaultsRun: true,
        created,
      });

      // Mark onboarding as complete
      await onboardingApi.completeOnboarding(selectedBusinessId, sessionId || undefined);

      goToStep('COMPLETE');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('onboarding.errors.saveFailed', 'Falha ao salvar dados'),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedBusinessId, sessionId, businessCategory, services, goToStep, t]);

  const handleGoToDashboard = useCallback(() => {
    navigate({ to: '/operator/dashboard' });
  }, [navigate]);

  const handleAbandon = useCallback(async () => {
    if (!sessionId) return;
    const confirmed = window.confirm(
      t(
        'onboarding.abandon.confirm',
        'Tem certeza que deseja cancelar o onboarding? Você poderá recomeçar a qualquer momento.',
      ),
    );
    if (!confirmed) return;

    try {
      await onboardingApi.abandonSession(sessionId);
      setSessionId(null);
      setCurrentStep('BUSINESS_PROFILE');
    } catch (err) {
      console.error('Failed to abandon session:', err);
    }
  }, [sessionId, t]);

  // Loading states
  if (authLoading) {
    return <LoadingSpinner />;
  }

  if (!operator) return null;

  if (!selectedBusinessId) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">
              {t(
                'onboarding.selectBusiness',
                'Selecione um negócio no menu para iniciar o onboarding.',
              )}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {t('onboarding.title', 'Onboarding')}
          </h1>
          <p className="text-muted-foreground">
            {selectedBusiness?.name && `${selectedBusiness.name} - `}
            {t('onboarding.subtitle', 'Configure seu negócio passo a passo')}
          </p>
        </div>
        {sessionId && currentStep !== 'COMPLETE' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleAbandon}
            className="gap-1.5 text-muted-foreground hover:text-destructive"
          >
            <X className="h-4 w-4" />
            {t('onboarding.abandon.button', 'Cancelar')}
          </Button>
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 shrink-0" />
          <p className="text-sm text-red-600 flex-1">{error}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setError(null)}
            className="shrink-0"
          >
            {t('common.close', 'Fechar')}
          </Button>
        </div>
      )}

      {/* Wizard */}
      <Card>
        <CardContent className="pt-6 pb-8">
          {/* Step Progress */}
          {currentStep !== 'COMPLETE' && (
            <div className="mb-8">
              <StepProgress currentStep={currentStep} steps={STEPS} />
            </div>
          )}

          {/* Step Content */}
          <div className="mt-4">
            {currentStep === 'BUSINESS_PROFILE' && (
              <BusinessProfileStep
                businessName={businessName}
                setBusinessName={setBusinessName}
                businessDescription={businessDescription}
                setBusinessDescription={setBusinessDescription}
                businessCategory={businessCategory}
                setBusinessCategory={setBusinessCategory}
                onNext={handleBusinessProfileNext}
                isSubmitting={isSubmitting}
              />
            )}

            {currentStep === 'CONTACT_INFO' && (
              <ContactInfoStep
                contactPhone={contactPhone}
                setContactPhone={setContactPhone}
                contactEmail={contactEmail}
                setContactEmail={setContactEmail}
                contactAddress={contactAddress}
                setContactAddress={setContactAddress}
                onNext={handleContactInfoNext}
                onBack={() => goToStep('BUSINESS_PROFILE')}
                isSubmitting={isSubmitting}
              />
            )}

            {currentStep === 'SERVICES' && (
              <ServicesStep
                services={services}
                setServices={setServices}
                onNext={handleServicesNext}
                onBack={() => goToStep('CONTACT_INFO')}
                isSubmitting={isSubmitting}
              />
            )}

            {currentStep === 'BUSINESS_HOURS' && (
              <BusinessHoursStep
                hours={hours}
                setHours={setHours}
                onNext={handleBusinessHoursNext}
                onBack={() => goToStep('SERVICES')}
                isSubmitting={isSubmitting}
              />
            )}

            {currentStep === 'TEAM_SETUP' && (
              <StaffSetupStep
                staffMembers={staffMembers}
                setStaffMembers={setStaffMembers}
                editingStaffIndex={editingStaffIndex}
                setEditingStaffIndex={setEditingStaffIndex}
                services={services}
                hours={hours}
                businessName={businessName || selectedBusiness?.name || ''}
                isLoadingStaff={isLoadingStaff}
                onNext={handleTeamSetupNext}
                onBack={() => goToStep('BUSINESS_HOURS')}
                isSubmitting={isSubmitting}
              />
            )}

            {currentStep === 'WHATSAPP_SETUP' && (
              <WhatsAppSetupStep
                onNext={handleWhatsAppNext}
                onBack={() => goToStep('TEAM_SETUP')}
              />
            )}

            {currentStep === 'TEMPLATES' && (
              <TemplatesStep
                templates={templates}
                isLoadingTemplates={isLoadingTemplates}
                onNext={handleTemplatesNext}
                onBack={() => goToStep('WHATSAPP_SETUP')}
                isSubmitting={isSubmitting}
              />
            )}

            {currentStep === 'COMPLETE' && (
              <CompleteStep
                businessName={businessName || selectedBusiness?.name || ''}
                servicesCount={completionServicesCount}
                hoursConfigured={Object.values(hours).some((h) => h.enabled)}
                staffCount={staffMembers.length}
                templatesCount={completionTemplatesCount}
                onGoToDashboard={handleGoToDashboard}
              />
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/_auth/operator/onboarding')({
  component: OnboardingPage,
});
